import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Skeleton,
  ListSubheader,
  Chip,
} from "@mui/material";
import { useWeddingWizard } from "../../../context/WeddingWizardContext";
import api from "../../../services/api";
import { useWeddingComposites } from "../../../hooks/useWeddingComposites";

function positionLabel(i, count) {
  if (count === 1) return "single";
  if (i === 0) return "bottom";
  if (i === count - 1) return "top";
  return "middle";
}

export default function FlavorsStep() {
  const { state, setCakeTiers, upsertCakeTier } = useWeddingWizard();
  const { indexByKey, loading: compsLoading } = useWeddingComposites();

  // selected composite (by key)
  const comp = useMemo(
    () => indexByKey.get(state.cake?.compositeKey) || null,
    [indexByKey, state.cake?.compositeKey]
  );

  useEffect(() => {
    const rawKey = state?.cake?.compositeKey;
    const trimmedKey = String(rawKey ?? "").trim();
    console.group("%c[FlavorsStep] key vs index", "color:#0a7");
    console.log("compositeKey (raw):", rawKey, `(type: ${typeof rawKey})`);
    console.log("compositeKey (trimmed):", trimmedKey);
    console.log("indexByKey.size:", indexByKey?.size);
    console.log("indexByKey.has(raw):", indexByKey?.has?.(rawKey));
    console.log("indexByKey.has(trimmed):", indexByKey?.has?.(trimmedKey));
    console.log("resolved comp:", comp?.key);
    console.groupEnd();
  }, [state?.cake?.compositeKey, indexByKey, comp]);

  const tiersRaw = Array.isArray(comp?.tiers) ? comp.tiers : [];
  // Order bottom→top by widest diameter first
  const tiersOrdered = useMemo(
    () =>
      [...tiersRaw].sort((a, b) => (b.diameterIn || 0) - (a.diameterIn || 0)),
    [tiersRaw]
  );
  const tierCount =
    comp?.tierCount ||
    (Array.isArray(tiersOrdered) ? tiersOrdered.length : 0) ||
    0;

  // Load wedding-compatible flavors once
  const [flavors, setFlavors] = useState([]);
  const [flavorsLoading, setFlavorsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setFlavorsLoading(true);
        const { data } = await api.get("/weddings/flavors");
        if (alive) setFlavors(Array.isArray(data?.flavors) ? data.flavors : []);
      } catch (e) {
        console.error("Failed to load flavors", e);
        if (alive) setFlavors([]);
      } finally {
        if (alive) setFlavorsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Build UI options that carry original docs (id + human-readable name)
  const flavorOptions = useMemo(
    () =>
      (flavors || []).map((f) => ({
        value: String(f._id),
        label: f.displayName || f.name || "Unnamed flavor",
        doc: f,
      })),
    [flavors]
  );

  // Build a quick set of available IDs to detect unavailable selections
  const availableIds = useMemo(
    () => new Set((flavorOptions || []).map((o) => o.value)),
    [flavorOptions]
  );

  // Ensure cake.tiers length matches tierCount (preserve existing flavorIds + snapshot when possible)
  useEffect(() => {
    if (!tierCount) return;
    const current = Array.isArray(state.cake?.tiers) ? state.cake.tiers : [];
    if (current.length === tierCount) return;

    const next = Array.from({ length: tierCount }, (_, i) => {
      const existing = current[i] || {};
      return {
        position: positionLabel(i, tierCount),
        flavorId: existing.flavorId ? String(existing.flavorId) : "",
        ...(existing.flavorNameSnapshot
          ? { flavorNameSnapshot: existing.flavorNameSnapshot }
          : {}),
        ...(Number.isFinite(existing.sizeInches)
          ? { sizeInches: existing.sizeInches }
          : {}),
      };
    });
    setCakeTiers(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierCount]);

  if (compsLoading) {
    return <Typography variant="body1">Loading…</Typography>;
  }

  if (!comp) {
    return (
      <Typography color="error">
        Cake configuration missing. Please go back and pick an option.
      </Typography>
    );
  }

  // --- Bucketing logic ---
  // Priority: isPremium → Premium bucket.
  // Else use effective upcharge: weddingUpchargePerTier (if set) else (basePriceIncluded ? 0 : price)
  const included = [];
  const extra = [];
  const premium = [];
  for (const f of flavors) {
    const upExplicit =
      f.weddingUpchargePerTier !== null &&
      f.weddingUpchargePerTier !== undefined
        ? Number(f.weddingUpchargePerTier) || 0
        : null;
    const effectiveUpcharge =
      upExplicit !== null
        ? upExplicit
        : f.basePriceIncluded
        ? 0
        : Number(f.price) || 0;

    if (f.isPremium) premium.push({ ...f, _up: effectiveUpcharge });
    else if (effectiveUpcharge > 0)
      extra.push({ ...f, _up: effectiveUpcharge });
    else included.push({ ...f, _up: 0 });
  }

  // Label per tier: Bottom / Middle / Top
  const tierName = (i) => {
    if (tierCount === 1) return "Single tier flavor";
    if (tierCount === 2)
      return i === 0 ? "Bottom tier flavor" : "Top tier flavor";
    if (i === 0) return "Bottom tier flavor";
    if (i === tierCount - 1) return "Top tier flavor";
    return "Middle tier flavor";
  };

  // Height cap must be on SelectProps.MenuProps.PaperProps
  const selectProps = {
    SelectProps: {
      MenuProps: {
        PaperProps: {
          sx: { maxHeight: 360, borderRadius: 2, mt: 1 },
          elevation: 3,
        },
      },
    },
  };

  // Render a list of plain MenuItems (no wrapper component)
  const renderMenuItems = (arr) =>
    arr.map((f) => {
      const up = Number(f._up ?? f.weddingUpchargePerTier ?? 0) || 0;
      const idStr = String(f._id);
      return (
        <MenuItem
          key={idStr}
          value={idStr}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <span style={{ flex: 1 }}>{f.displayName || f.name}</span>
          {up > 0 && (
            <Typography variant="body2" color="text.secondary">
              +${up.toFixed(2)}
            </Typography>
          )}
          {f.isPremium && (
            <Chip
              size="small"
              label="Premium"
              sx={{ bgcolor: "error.light", color: "error.contrastText" }}
            />
          )}
        </MenuItem>
      );
    });

  // onChange → pass BOTH id and name snapshot
  const handleFlavorChange = (position) => (e) => {
    const val = String(e.target.value); // flavorId
    const opt = flavorOptions.find((o) => o.value === val);
    upsertCakeTier({
      position,
      flavorId: val,
      flavorName: opt?.label, // snapshot so totals / review can show human name
    });
  };

  // Current flavors per tier from state
  const currentTiers = Array.isArray(state.cake?.tiers) ? state.cake.tiers : [];

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Choose flavors
      </Typography>

      {flavorsLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: tierCount }).map((_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={56} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {Array.from({ length: tierCount }).map((_, i) => {
            const currentId = String(currentTiers?.[i]?.flavorId || "");
            const currentName =
              currentTiers?.[i]?.flavorNameSnapshot || currentId;
            const isUnavailable = currentId && !availableIds.has(currentId);

            return (
              <Grid item xs={12} sm={6} key={i}>
                <TextField
                  select
                  fullWidth
                  label={tierName(i)}
                  value={currentId}
                  onChange={handleFlavorChange(positionLabel(i, tierCount))}
                  {...selectProps}>
                  {/* Placeholder */}
                  <MenuItem value="">
                    <em>Select a flavor</em>
                  </MenuItem>

                  {included.length > 0 && (
                    <ListSubheader
                      disableSticky
                      sx={{ bgcolor: "background.paper", fontWeight: 600 }}>
                      Included Flavors
                    </ListSubheader>
                  )}
                  {renderMenuItems(included)}

                  {extra.length > 0 && (
                    <ListSubheader
                      disableSticky
                      sx={{
                        bgcolor: "background.paper",
                        fontWeight: 600,
                        mt: 0.5,
                      }}>
                      Extra Flavors
                    </ListSubheader>
                  )}
                  {renderMenuItems(extra)}

                  {premium.length > 0 && (
                    <ListSubheader
                      disableSticky
                      sx={{
                        bgcolor: "background.paper",
                        fontWeight: 600,
                        mt: 0.5,
                      }}>
                      Premium Flavors
                    </ListSubheader>
                  )}
                  {renderMenuItems(premium)}

                  {/* If the previously selected flavor no longer exists, show it as disabled so the value isn't "invisible" */}
                  {isUnavailable && (
                    <MenuItem value={currentId} disabled>
                      {`${currentName} (unavailable)`}
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1.25 }}>
        One flavor per tier. Any upcharge applies per tier.
      </Typography>
    </Box>
  );
}
